import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  createFilterOptions,
  ListItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as logger from "common/logger";
import FileDetail from "components/templates/FileDetail";
import { brandTypeStyle } from "components/templates/templateTypeStyle";
import * as fileUtils from "fileUtils";
import { useRef, useState } from "react";
import { useListTemplatesQuery } from "services/docGenService";
import { useConfigQuery } from "services/staticDataService";

export const EDITABLE_TEMPLATE_FORMS = new Set([
  "WORD",
  "WORD_JINJA",
  "WORD_JINJA_ENDORSEMENT",
  "ENDORSEABLE_WORD_JINJA",
  "PDF_ENDORSEMENT",
  "ENDORSABLE_PDF",
]);

const NON_EDITABLE_TOOLTIP =
  "This template format isn't supported in the editor. Use the upload option to send a copy.";

// Match against title and filename together, plus underscore-stripped
// versions, so typing "nuclear" finds "NMA_1998 - Nuclear Incident
// Exclusion Clause.docx" and typing "NMA 1998" matches "NMA_1998".
const filterTemplateOptions = createFilterOptions({
  stringify: (option) => {
    const title = option?.title ?? "";
    const filename = option?.filename ?? "";
    return [
      title,
      filename,
      title.replace(/_/g, " "),
      filename.replace(/_/g, " "),
    ].join(" ");
  },
});

// Titles of endorsement keys flagged `deprecated: true` in the company's
// endorsements config. Deprecation lives on the endorsement key, not the
// template row, so join by title — the same join the policy page's
// endorsement selector uses.
export const makeDeprecatedTitleSet = (endorsementsConfig) =>
  new Set(
    Object.entries(endorsementsConfig?.items ?? {})
      .filter(([, item]) => !!item?.deprecated)
      .map(([title]) => title)
  );

const fetchTemplateAsFile = async (template) => {
  const url = await fileUtils.getDownloadUrl(template.file_id);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download template (${response.status})`);
  }
  const blob = await response.blob();
  const filename = template.filename || `${template.title || "template"}.docx`;
  return new File([blob], filename, { type: blob.type });
};

/**
 * Autocomplete over the instance's templates for the "Edit Existing Template"
 * flow. Calling `onSelect` invokes the parent with:
 *   { file: File, s3FileId: string, templateId: string, templateTitle: string }
 * where `file` is the template's bytes wrapped as a browser File, and
 * `s3FileId` is the template's existing `file_id` so the upstream request
 * can reuse it without re-uploading.
 *
 * Templates whose endorsement key is deprecated are omitted entirely so a
 * request can't be raised against a form that is no longer in use.
 */
const ExistingTemplatePicker = ({ onSelect, disabled = false }) => {
  const {
    data: templates,
    isFetching,
    error: listError,
  } = useListTemplatesQuery();
  // If the endorsements config can't be loaded we fall back to the unfiltered
  // list rather than blocking the portal — the worst case is the old behaviour.
  const {
    data: endorsementsConfig,
    isLoading: isLoadingConfig,
  } = useConfigQuery("endorsements");
  const [loadingTemplateId, setLoadingTemplateId] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  // Monotonic counter used to detect stale fetches. If the user picks a second
  // template before the first fetch resolves, the first's resolution is
  // discarded — only the latest pick can call onSelect.
  const requestCounterRef = useRef(0);

  const deprecatedTitles = makeDeprecatedTitleSet(endorsementsConfig);
  const isLoading = isFetching || isLoadingConfig;

  // Hold the options back until the config has resolved so a deprecated
  // template can't be picked in the window before we know it's deprecated.
  const options = (isLoadingConfig ? [] : templates ?? [])
    .filter((t) => t?.id && !deprecatedTitles.has(t.title))
    .map((t) => ({
      ...t,
      _editable: EDITABLE_TEMPLATE_FORMS.has(t.form) && !!t.file_id,
    }))
    .sort((a, b) => {
      if (a._editable !== b._editable) return a._editable ? -1 : 1;
      return (a.title ?? "").localeCompare(b.title ?? "");
    });

  const handleChange = async (_event, template) => {
    if (!template) return;
    if (!template._editable) return;
    setFetchError(null);
    setLoadingTemplateId(template.id);
    const myRequestId = ++requestCounterRef.current;
    try {
      const file = await fetchTemplateAsFile(template);
      if (myRequestId !== requestCounterRef.current) return;
      onSelect?.({
        file,
        s3FileId: template.file_id,
        templateId: template.id,
        templateTitle: template.title,
      });
    } catch (error) {
      if (myRequestId !== requestCounterRef.current) return;
      logger.exception(error, {
        breadcrumb: { templateId: template.id, fileId: template.file_id },
      });
      setFetchError(
        `Could not load "${
          template.title || template.filename
        }". Try uploading instead.`
      );
    } finally {
      if (myRequestId === requestCounterRef.current) {
        setLoadingTemplateId(null);
      }
    }
  };

  if (listError) {
    return (
      <Alert severity={"error"}>
        {"Could not load the template list. Try again or upload a file."}
      </Alert>
    );
  }

  const renderOption = (props, option) => {
    const row = (
      <ListItem
        {...props}
        key={option.id}
        disabled={!option._editable}
        sx={{
          opacity: option._editable ? 1 : 0.55,
          cursor: option._editable ? "pointer" : "not-allowed",
        }}
      >
        <FileDetail
          name={option.title}
          filename={option.filename}
          type={option.form}
          typeStyle={brandTypeStyle}
        />
      </ListItem>
    );
    if (!option._editable) {
      return (
        <Tooltip
          title={NON_EDITABLE_TOOLTIP}
          placement={"left"}
          key={option.id}
        >
          <span>{row}</span>
        </Tooltip>
      );
    }
    return row;
  };

  return (
    <Stack gap={1.5}>
      <Autocomplete
        options={options}
        loading={isLoading}
        disabled={disabled}
        getOptionLabel={(option) => option?.title || option?.filename || ""}
        getOptionDisabled={(option) => !option._editable}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        filterOptions={filterTemplateOptions}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            label={"Search templates"}
            placeholder={"Start typing a template name or filename"}
            variant={"outlined"}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingTemplateId ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        loadingText={"Loading templates..."}
        noOptionsText={
          isLoading ? "Loading templates..." : "No templates available yet"
        }
        onChange={handleChange}
        clearOnBlur
        blurOnSelect
      />
      {fetchError && (
        <Alert severity={"warning"} onClose={() => setFetchError(null)}>
          {fetchError}
        </Alert>
      )}
      {loadingTemplateId && (
        <Box display={"flex"} alignItems={"center"} gap={1}>
          <CircularProgress size={16} />
          <Typography variant={"body2"} color={"text.secondary"}>
            {"Loading template into editor..."}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default ExistingTemplatePicker;
